import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import "./Leads.scss";
import { toast } from "react-toastify";
import IconButton from "../../components/IconButton/IconButton";
import GeneralTable from "../../components/Table/GeneralTable";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import WarningPopup from "../../components/WarningPopup/WarningPopup";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";

const Leads = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLeadId, setEditingLeadId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("all");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignLeadId, setReassignLeadId] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedReassignEmployeeId, setSelectedReassignEmployeeId] =
    useState("");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    leadSource: "CAMPAIGN",
    leadStatus: "AVAILABLE",
    email: "",
    phoneNumber: "",
    companyName: "",
    jobTitle: "",
    industry: "",
    website: "",
    notes: "",
    tags: "",
    leadScore: 0,
    assignedToId: "",
    category: "CASH",
    contactPersons: [],
  });
  const [bulkLeads, setBulkLeads] = useState([]);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const isSalesManager = user?.authorities?.includes("sales-manager");

  const leadSources = ["CAMPAIGN", "REFERRAL", "INBOUND", "OUTBOUND", "OTHER"];
  const categories = ["CASH", "CREDIT"];

  const formatText = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  // Fetch employees under the sales manager
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/sales/list`,
      );
      if (response.data.status === 200 && response.data.data) {
        setEmployees(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  }, []);

  useEffect(() => {
    if (user && isSalesManager) {
      fetchEmployees();
    }
  }, [user, isSalesManager, fetchEmployees]);

  const fetchAllEmployees = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/list`,
      );
      if (response.data.status === 200 && response.data.data) {
        setAllEmployees(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching all employees:", err);
    }
  }, []);

  const downloadTemplate = async () => {
    try {
      setLoading(true);

      const endpoint = isSalesManager
        ? `${process.env.REACT_APP_SALES_MANAGER}/lead/template/crm/download-template`
        : `${process.env.REACT_APP_SALES}/lead/template/crm/download-template`;

      const response = await axiosInstance.get(endpoint, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "lead_upload_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Template downloaded successfully!");
    } catch (error) {
      console.error("Error downloading template:", error);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setUploadSuccess(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
        setUploadSuccess(false);
      } else {
        toast.error("Please select a valid Excel file (.xlsx or .xls)");
        e.target.value = null;
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);

      const endpoint = isSalesManager
        ? `${process.env.REACT_APP_SALES_MANAGER}/lead/template/crm/upload`
        : `${process.env.REACT_APP_SALES}/lead/template/crm/upload`;

      const response = await axiosInstance.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === 200) {
        setUploadSuccess(true);
        toast.success("Leads uploaded successfully!");
        await fetchLeads(
          selectedEmployeeId === "all" ? null : selectedEmployeeId,
        );

        setTimeout(() => {
          closeModal();
        }, 2000);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch leads based on role - wrapped in useCallback
  const fetchLeads = useCallback(
    async (employeeId = null) => {
      setLoading(true);
      setError(null);
      try {
        let url;

        if (isSalesManager) {
          if (employeeId && employeeId !== "all") {
            url = `${process.env.REACT_APP_SALES}/leads/assigned-leads/${employeeId}`;
          } else {
            url = `${process.env.REACT_APP_SALES}/leads/assigned-leads/new`;
          }
        } else {
          url = `${process.env.REACT_APP_SALES}/leads/assigned-leads/new`;
        }

        const response = await axiosInstance.get(url);
        if (response.data.status === 200 && response.data.data) {
          setLeads(response.data.data);
        } else {
          setError("Failed to fetch leads");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch leads");
      } finally {
        setLoading(false);
      }
    },
    [isSalesManager],
  );

  useEffect(() => {
    if (user) {
      fetchLeads(selectedEmployeeId === "all" ? null : selectedEmployeeId);
    }
  }, [user, selectedEmployeeId, fetchLeads]);

  const fetchLeadById = async (id) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SALES}/leads/${id}`,
      );
      if (response.data.status === 200 && response.data.data) {
        return response.data.data;
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch lead details");
    }
  };

  const createLead = async (leadData) => {
    setLoading(true);
    try {
      const correctedLeadData = {
        ...leadData,
        contactPerson: leadData.contactPersons,
      };

      delete correctedLeadData.contactPersons; // IMPORTANT

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SALES}/leads`,
        correctedLeadData,
      );

      console.log("Create lead response:", response);

      if (response.status === 200) {
        await fetchLeads(
          selectedEmployeeId === "all" ? null : selectedEmployeeId,
        );
        toast.success("Lead created successfully!");
        resetForm();
        setShowAddForm(false);
      } else {
        setError("Failed to create lead");
      }
    } catch (err) {
      console.log(err.response?.data?.message || "Failed to create lead");
    } finally {
      setLoading(false);
    }
  };

  const validateContactPersons = (contactPersons) => {
    if (!contactPersons || contactPersons.length === 0) {
      toast.error("At least one contact person is required");
      return false;
    }

    for (let i = 0; i < contactPersons.length; i++) {
      const contact = contactPersons[i];

      // 🔴 Mandatory mobile number
      if (!contact.mobileNumber) {
        toast.error(`Contact person ${i + 1}: Mobile number is mandatory`);
        return false;
      }

      if (contact.mobileNumber.length < 10) {
        toast.error(
          `Contact person ${i + 1}: Mobile number must be at least 10 digits`,
        );
        return false;
      }

      if (contact.whatsAppNumber && contact.whatsAppNumber.length < 10) {
        toast.error(
          `Contact person ${i + 1}: WhatsApp number must be at least 10 digits`,
        );
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSalesManager && !formData.assignedToId) {
      toast.info("Please assign this lead to an employee!");
      return;
    }

    // 🔴 Contact person mobile validation
    if (!validateContactPersons(formData.contactPersons)) {
      return;
    }

    if (editingLeadId) {
      await updateLead({ ...formData, id: editingLeadId });
    } else {
      await createLead({
        ...formData,
        contactPerson: formData.contactPersons,
      });
    }
  };

  const updateLead = async (leadData) => {
    try {
      setLoading(true);

      const correctedLeadData = {
        ...leadData,
        contactPerson: leadData.contactPersons,
      };

      delete correctedLeadData.contactPersons;

      const response = await axiosInstance.put(
        `${process.env.REACT_APP_SALES}/leads`,
        correctedLeadData,
      );

      console.log("Update lead response:", response);

      if (response.data.status === 200) {
        await fetchLeads(
          selectedEmployeeId === "all" ? null : selectedEmployeeId,
        );
        toast.success("Lead updated successfully!");
        setEditingLeadId(null);
        resetForm();
        setShowAddForm(false);
      }

      return response.data;
    } catch (error) {
      console.error("Error updating lead:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const assignLeadToEmployee = async (leadId, employeeId) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_SALES}/leads/assign/${leadId}/employee/${employeeId}`,
      );
      if (response.data.status === 200) {
        await fetchLeads(
          selectedEmployeeId === "all" ? null : selectedEmployeeId,
        );
        toast.success("Lead assigned successfully!");
      }
      return response.data;
    } catch (error) {
      console.error("Error assigning lead:", error);
      throw error;
    }
  };

  const handleCreateEnquiryFromLead = (lead) => {
    if (!lead?.id) {
      toast.error("Invalid lead selected");
      return;
    }

    navigate("/enquiries", {
      state: {
        autoCreateFromLead: true,
        leadId: lead.id,
      },
    });
  };

  const handleReassignClick = async (leadId) => {
    setReassignLeadId(leadId);
    setSelectedReassignEmployeeId("");
    if (allEmployees.length === 0) await fetchAllEmployees();
    setShowReassignModal(true);
  };

  const handleReassignSubmit = async () => {
    if (!selectedReassignEmployeeId) {
      toast.error("Please select an employee");
      return;
    }
    try {
      await assignLeadToEmployee(
        reassignLeadId,
        parseInt(selectedReassignEmployeeId),
      );
      setShowReassignModal(false);
      setReassignLeadId(null);
    } catch (err) {
      console.error("Reassign failed:", err);
    }
  };

  const renderActionButtons = (lead) => (
    <div className="action-buttons">
      <IconButton
        icon="mdi:file-plus"
        onClick={(e) => {
          e.stopPropagation();
          handleCreateEnquiryFromLead(lead);
        }}
        variant="primary"
        title="Create Enquiry"
      />
      <IconButton
        icon="mdi:account-switch" // ← NEW
        onClick={(e) => {
          e.stopPropagation();
          handleReassignClick(lead.id);
        }}
        variant="edit"
        title="Reassign Lead"
      />
      <IconButton
        icon="iconamoon:edit-light"
        onClick={(e) => {
          e.stopPropagation();
          handleEditLead(lead);
        }}
        variant="edit"
        title="Edit Lead"
      />
      <IconButton
        icon="mdi:delete"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteClick(lead.id);
        }}
        variant="delete"
        title="Delete Lead"
      />
    </div>
  );

  const handleEditLead = async (lead) => {
    try {
      if (!lead || !lead.id) {
        toast.error("Invalid lead selected");
        return;
      }

      setLoading(true);
      const fullLead = await fetchLeadById(lead.id);
      if (fullLead) {
        setFormData(fullLead);
        setEditingLeadId(lead.id);
        setShowAddForm(true);
      }
    } catch (error) {
      console.error("Error loading lead:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAssignment = async (leadId, employeeId) => {
    try {
      if (!leadId || !employeeId) {
        toast.error("Invalid lead or employee selection");
        return;
      }
      await assignLeadToEmployee(leadId, employeeId);
    } catch (error) {
      console.error("Failed to assign lead:", error);
    }
  };

  const handleDeleteLead = async () => {
    if (!leadToDelete) return;

    try {
      setLoading(true);
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_SALES}/leads/${leadToDelete}`,
      );

      if (response.data.status === 200) {
        await fetchLeads(
          selectedEmployeeId === "all" ? null : selectedEmployeeId,
        );
        toast.success("Lead deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting lead:", error);
    } finally {
      setLoading(false);
      setShowWarningPopup(false);
      setLeadToDelete(null);
    }
  };

  const createBulkLeads = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_SALES}/leads/bulk`,
        bulkLeads,
      );
      if (response.data.status === 200) {
        await fetchLeads(
          selectedEmployeeId === "all" ? null : selectedEmployeeId,
        );
        setBulkLeads([]);
        toast.success("Bulk leads created successfully!");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create bulk leads");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      leadSource: "CAMPAIGN",
      leadStatus: "AVAILABLE",
      email: "",
      phoneNumber: "",
      companyName: "",
      jobTitle: "",
      industry: "",
      website: "",
      notes: "",
      tags: "",
      leadScore: 0,
      assignedToId: "",
      category: "CASH",
      contactPersons: [
        {
          contactPersonName: "",
          email: "",
          whatsAppNumber: "",
          mobileNumber: "",
        },
      ],
    });
  };

  const handleDeleteClick = (leadId) => {
    setLeadToDelete(leadId);
    setShowWarningPopup(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "assignedToId" ? Number(value) : value,
    }));
  };

  const renderEmailCell = (email) => {
    if (!email) return "";
    return (
      <a
        href={`mailto:${email}`}
        className="email-link"
        onClick={(e) => e.stopPropagation()}
      >
        {email}
      </a>
    );
  };

  const renderWebsiteCell = (website) => {
    if (!website) return "";
    const url = website.startsWith("http") ? website : `https://${website}`;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="website-link"
        onClick={(e) => e.stopPropagation()}
      >
        {website}
      </a>
    );
  };

  const renderStatusCard = (value, type) => {
    if (!value) return "";
    const formattedValue = formatText(value);
    const className = `status-card ${type}-status ${value.toLowerCase()}`;
    return <span className={className}>{formattedValue}</span>;
  };

  const renderTagsCell = (tags) => {
    if (!tags) return "—";
    const tagArray = tags
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag);
    if (tagArray.length === 0) return "—";

    return (
      <div className="tags-container">
        {tagArray.map((tag, index) => (
          <span key={index} className="tag-chip">
            {tag}
          </span>
        ))}
      </div>
    );
  };

  const renderScoreCell = (score) => {
    if (score === null || score === undefined) return "—";

    const numScore = Number(score);
    const maxStars = 5;
    const filledStars = Math.round(numScore);

    let scoreClass = "score-low";
    if (numScore >= 4) scoreClass = "score-high";
    else if (numScore >= 3) scoreClass = "score-medium";

    return (
      <div className="score-container">
        <div className="score-stars">
          {Array.from({ length: maxStars }, (_, index) => (
            <span
              key={index}
              className={`star ${
                index < filledStars ? "filled" : "empty"
              } ${scoreClass}`}
            >
              ★
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderAssignedToColumn = (lead) => {
    if (isSalesManager) {
      return (
        <select
          className="assigned-to-select"
          value={lead.assignedToId || ""}
          onChange={(e) => {
            if (e.target.value) {
              handleSaveAssignment(lead.id, parseInt(e.target.value));
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="">Unassigned</option>
          {employees.map((emp) => (
            <option key={emp.empId} value={emp.empId}>
              {emp.firstName} {emp.lastName}
            </option>
          ))}
        </select>
      );
    }
    return <span>{lead.assignedToName || "—"}</span>;
  };

  const allLeadsHeaders = [
    {
      label: "Company Name",
      key: "companyName",
    },
    {
      label: "Email",
      key: "email",
      render: renderEmailCell,
    },
    { label: "Phone", key: "phoneNumber" },
    { label: "Job Title", key: "jobTitle" },
    { label: "Industry", key: "industry" },
    {
      label: "Website",
      key: "website",
      render: renderWebsiteCell,
    },
    {
      label: "Category",
      key: "category",
      render: (value) => renderStatusCard(value, "category"),
    },
    {
      label: "Source",
      key: "leadSource",
      render: (value) => renderStatusCard(value, "source"),
    },
    {
      label: "Tags",
      key: "tags",
      render: renderTagsCell,
    },
    {
      label: "Score",
      key: "leadScore",
      render: renderScoreCell,
    },
    {
      label: "Assigned To",
      key: "assignedTo",
      render: (value, lead) => renderAssignedToColumn(lead),
    },
    { label: "Notes", key: "notes" },
    {
      label: "Actions",
      key: "actions",
      render: (value, lead) => renderActionButtons(lead),
      type: "action",
      actionType: "custom",
    },
  ];

  const allLeadsColumns = allLeadsHeaders.map((col) => ({
    header: col.label,
    accessor: col.key,
    render: col.render,
    type: col.type,
    actionType: col.actionType,
  }));

  const allLeadsData = leads.map((lead, index) => ({
    ...lead,
    companyName: `${index + 1}. ${lead.companyName}`,
    actions: lead,
  }));

  const allLeadsDataSafe = Array.isArray(allLeadsData) ? allLeadsData : [];

  if (loading && leads.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="leads-container">
      <div className="header">
        <div className="header-left">
          <h1>Sales Leads Management</h1>
          <p className="subtitle">
            Track, manage, and follow up on all sales leads efficiently.
          </p>
        </div>
        <div className="header-actions">
          <ButtonWithIcon
            icon="mdi:plus"
            text="Add New Lead"
            className="submit-button"
            onClick={() => {
              resetForm();
              setEditingLeadId(null);
              setShowAddForm(true);
            }}
          />

          <ButtonWithIcon
            icon="mdi:file-upload"
            text="Upload Excel"
            onClick={() => setShowUploadModal(true)}
          />

          <ButtonWithIcon
            icon="tdesign:send"
            text={`Import ${bulkLeads.length} Leads`}
            onClick={createBulkLeads}
            disabled={bulkLeads.length === 0 || loading}
          />
        </div>
      </div>

      {isSalesManager && employees.length > 0 && (
        <div className="filter-section">
          <label htmlFor="employee-filter">Filter by Employee:</label>
          <select
            id="employee-filter"
            value={selectedEmployeeId}
            onChange={(e) => setSelectedEmployeeId(e.target.value)}
            className="employee-filter-select"
          >
            <option value="all">All Employees</option>
            {employees.map((emp) => (
              <option key={emp.empId} value={emp.empId}>
                {emp.firstName} {emp.lastName}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">
            ×
          </button>
        </div>
      )}

      {showUploadModal && (
        <div className="upload-modal-overlay">
          <div className="upload-modal">
            <div className="modal-header">
              <h3>Bulk Upload Lead Details</h3>
              <button
                className="close-button"
                onClick={closeModal}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>

            <div className="modal-subheader">
              <p>
                Download the template, fill in the details, and upload the file
                below.
              </p>
            </div>

            <div className="modal-body">
              {uploadSuccess ? (
                <div className="success-message">
                  <i className="fas fa-check-circle success-icon"></i>
                  <p>File uploaded successfully!</p>
                </div>
              ) : (
                <>
                  <button
                    className="download-template-button"
                    onClick={downloadTemplate}
                  >
                    <i className="fas fa-download"></i> Download Template
                  </button>

                  <div className="file-input-container">
                    <label
                      htmlFor="lead-file-upload"
                      className="file-upload-label"
                    >
                      <i className="fas fa-cloud-upload-alt"></i>
                      {selectedFile
                        ? selectedFile.name
                        : "Click to choose Excel file"}
                    </label>
                    <input
                      id="lead-file-upload"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                  </div>

                  <button
                    className="upload-button"
                    onClick={handleUpload}
                    disabled={!selectedFile || loading}
                  >
                    <i className="fas fa-upload"></i>{" "}
                    {loading ? "Uploading..." : "Upload File"}
                  </button>

                  <button className="cancel-button" onClick={closeModal}>
                    <i className="fas fa-times-circle"></i> Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="add-form-container">
          <div className="form-header">
            <h3>{editingLeadId ? "Edit Lead" : "Add New Lead"}</h3>
          </div>
          <form onSubmit={handleSubmit} className="compact-form">
            {/* Row 1: 5 fields */}
            <div className="form-row medium-row">
              <div className="form-group compact">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group compact">
                <label>Company Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group compact">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group compact">
                <label>Industry</label>
                <input
                  type="text"
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group compact">
                <label>Job Title</label>
                <input
                  type="text"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Row 2: 4 fields */}
            <div className="form-row medium-row">
              <div className="form-group compact">
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group compact">
                <label>Category *</label>
                <div className="button-group">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`btn-option ${
                        formData.category === cat ? "active" : ""
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, category: cat }))
                      }
                    >
                      {formatText(cat)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group compact">
                <label>Score</label>
                <div className="button-group">
                  {Array.from({ length: 5 }, (_, i) => i + 1).map((score) => (
                    <button
                      key={score}
                      type="button"
                      className={`btn-option ${
                        formData.leadScore === score ? "active" : ""
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, leadScore: score }))
                      }
                    >
                      {score}
                    </button>
                  ))}
                </div>
              </div>

              {isSalesManager && (
                <div className="form-group compact">
                  <label>Assigned To</label>
                  <select
                    name="assignedToId"
                    value={formData.assignedToId}
                    onChange={handleInputChange}
                    required={isSalesManager}
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.empId} value={emp.empId}>
                        {emp.firstName} {emp.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group compact">
                <label>Source *</label>
                <div className="button-group">
                  {leadSources.map((source) => (
                    <button
                      key={source}
                      type="button"
                      className={`btn-option ${
                        formData.leadSource === source ? "active" : ""
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, leadSource: source }))
                      }
                    >
                      {formatText(source)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group compact">
                <label>Tags</label>
                <textarea
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Comma separated tags"
                />
              </div>
            </div>

            {/* Row 4: Tags and Notes - 2 columns */}
            <div className="form-row">
              <div className="form-group compact">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Additional notes about this lead..."
                />
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="contact-form">
              <div className="contact-header">
                <h6>Contact Information</h6>
                <ButtonWithIcon
                  type="button"
                  icon="mdi:account-plus"
                  text="Add Contact"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      contactPersons: [
                        ...prev.contactPersons,
                        {
                          contactPersonName: "",
                          email: "",
                          whatsAppNumber: "",
                          mobileNumber: "",
                        },
                      ],
                    }));
                  }}
                  variant="secondary"
                  size="sm"
                />
              </div>

              {formData.contactPersons.map((contact, index) => (
                <div key={index} className="contact-person-row">
                  <div className="contact-person-header">
                    {formData.contactPersons.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger remove-contact"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: prev.contactPersons.filter(
                              (_, i) => i !== index,
                            ),
                          }));
                        }}
                        title="Remove contact person"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="form-row large-row">
                    <div className="form-group compact">
                      <input
                        type="text"
                        placeholder="Contact Name"
                        value={contact.contactPersonName}
                        onChange={(e) => {
                          const newContacts = [...formData.contactPersons];
                          newContacts[index].contactPersonName = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: newContacts,
                          }));
                        }}
                      />
                    </div>
                    <div className="form-group compact">
                      <input
                        type="email"
                        placeholder="Email"
                        value={contact.email}
                        onChange={(e) => {
                          const newContacts = [...formData.contactPersons];
                          newContacts[index].email = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: newContacts,
                          }));
                        }}
                      />
                    </div>
                    <div className="form-group compact">
                      <input
                        type="tel"
                        placeholder="Mobile Number *"
                        value={contact.mobileNumber}
                        onChange={(e) => {
                          const newContacts = [...formData.contactPersons];
                          newContacts[index].mobileNumber =
                            e.target.value.replace(/\D/g, "");
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: newContacts,
                          }));
                        }}
                        maxLength="15"
                        required
                      />
                    </div>
                    <div className="form-group compact whatsapp-copy">
                      <input
                        type="tel"
                        placeholder="WhatsApp Number"
                        value={contact.whatsAppNumber}
                        onChange={(e) => {
                          const newContacts = [...formData.contactPersons];
                          newContacts[index].whatsAppNumber =
                            e.target.value.replace(/\D/g, "");
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: newContacts,
                          }));
                        }}
                      />

                      <button
                        type="button"
                        className="copy-btn"
                        title="Copy mobile number to WhatsApp"
                        onClick={() => {
                          const newContacts = [...formData.contactPersons];
                          newContacts[index].whatsAppNumber =
                            newContacts[index].mobileNumber || "";
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: newContacts,
                          }));
                        }}
                      >
                        <Icon icon="mdi:content-copy" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="form-actions">
              <ButtonWithIcon
                type="button"
                icon="mdi:cancel"
                text="Cancel"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingLeadId(null);
                  resetForm();
                }}
                className="cancel-button"
              />
              <ButtonWithIcon
                type="submit"
                icon="tdesign:send"
                className="submit-button"
                text={
                  editingLeadId
                    ? loading
                      ? "Updating..."
                      : "Update Lead"
                    : loading
                      ? "Creating..."
                      : "Create Lead"
                }
                disabled={loading}
              />
            </div>
          </form>
        </div>
      )}

      <div className="tab-content">
        <GeneralTable
          columns={allLeadsColumns}
          data={allLeadsDataSafe}
          title={
            isSalesManager
              ? selectedEmployeeId === "all"
                ? "All Team Leads"
                : `Leads for ${
                    employees.find(
                      (e) => e.empId === parseInt(selectedEmployeeId),
                    )?.firstName
                  } ${
                    employees.find(
                      (e) => e.empId === parseInt(selectedEmployeeId),
                    )?.lastName
                  }`
              : "My Assigned Leads"
          }
        />
      </div>

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={handleDeleteLead}
        onDecline={() => {
          setShowWarningPopup(false);
          setLeadToDelete(null);
        }}
        title="Delete Lead"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        acceptText="Yes, Delete"
        declineText="Cancel"
      />

      {showReassignModal && (
        <div className="upload-modal-overlay">
          <div className="upload-modal">
            <div className="modal-header">
              <h3>Reassign Lead</h3>
              <button
                className="close-button"
                onClick={() => setShowReassignModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Select an employee to reassign this lead to:</p>
              <select
                className="assigned-to-select"
                value={selectedReassignEmployeeId}
                onChange={(e) => setSelectedReassignEmployeeId(e.target.value)}
              >
                <option value="">-- Select Employee --</option>
                {allEmployees
                  .filter((emp) => emp.empId !== user?.empId)
                  .map((emp) => (
                    <option key={emp.empId} value={emp.empId}>
                      {emp.firstName} {emp.lastName} — {emp.companyEmail}
                    </option>
                  ))}
              </select>
              <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                <ButtonWithIcon
                  icon="tdesign:send"
                  text="Reassign"
                  className="submit-button"
                  onClick={handleReassignSubmit}
                  disabled={loading}
                />
                <ButtonWithIcon
                  icon="mdi:cancel"
                  text="Cancel"
                  className="cancel-button"
                  onClick={() => setShowReassignModal(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
